import { ClipboardList, FileText, LayoutGrid, Search, X } from 'lucide-react';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { searchService, SearchResult, SearchResultType } from '@/services/searchService';

interface GlobalSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TYPE_CONFIG: Record<SearchResultType, { icon: React.ElementType; labelKey: string; color: string }> = {
  domain: {
    icon: LayoutGrid,
    labelKey: 'search.group.domains',
    color: 'text-blue-500',
  },
  use_case: {
    icon: ClipboardList,
    labelKey: 'search.group.useCases',
    color: 'text-purple-500',
  },
  cadrage: {
    icon: FileText,
    labelKey: 'search.group.cadrages',
    color: 'text-orange-500',
  },
};

const ORDER: SearchResultType[] = ['domain', 'use_case', 'cadrage'];

function groupResults(results: SearchResult[]): Record<SearchResultType, SearchResult[]> {
  const grouped = {} as Record<SearchResultType, SearchResult[]>;
  for (const type of ORDER) grouped[type] = [];
  for (const r of results) {
    if (grouped[r.type]) grouped[r.type].push(r);
  }
  return grouped;
}

export const GlobalSearch: React.FC<GlobalSearchProps> = ({ open, onOpenChange }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flatResults = results;

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setResults([]);
      setActiveIndex(-1);
    }
  }, [open]);

  const doSearch = useCallback(async (q: string) => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setIsLoading(true);
    try {
      const data = await searchService.globalSearch(q);
      setResults(data.results);
      setActiveIndex(-1);
    } catch {
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      doSearch(query);
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, doSearch]);

  const handleSelect = (result: SearchResult) => {
    navigate(result.url);
    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!flatResults.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(flatResults[activeIndex]);
    } else if (e.key === 'Escape') {
      onOpenChange(false);
    }
  };

  if (!open) return null;

  const grouped = groupResults(results);
  const hasResults = results.length > 0;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center pt-[10vh]"
      onClick={() => onOpenChange(false)}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in" />

      {/* Dialog */}
      <div
        className="relative w-full max-w-xl mx-4 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="glass-card rounded-xl shadow-2xl overflow-hidden border-border/60">
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
            <Search className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              placeholder={t('search.placeholder', 'Search domains, use cases, solutions, users...')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 bg-transparent text-foreground placeholder:text-muted-foreground outline-none text-sm"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground border border-border rounded">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div className="max-h-[60vh] overflow-y-auto">
            {isLoading && (
              <div className="py-8 text-center">
                <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            )}

            {!isLoading && query && !hasResults && (
              <div className="py-10 text-center text-muted-foreground text-sm">
                {t('search.noResults', 'No results for')} &ldquo;{query}&rdquo;
              </div>
            )}

            {!isLoading && !query && (
              <div className="py-10 text-center text-muted-foreground text-sm">
                {t('search.hint', 'Type to search across your workspace')}
              </div>
            )}

            {!isLoading && hasResults && (
              <div className="py-2">
                {ORDER.map((type) => {
                  const group = grouped[type];
                  if (!group.length) return null;
                  const config = TYPE_CONFIG[type];
                  const Icon = config.icon;

                  return (
                    <div key={type}>
                      {/* Group header */}
                      <div className="px-4 py-1.5 flex items-center gap-2">
                        <Icon className={cn('h-3.5 w-3.5', config.color)} />
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          {t(config.labelKey, type)}
                        </span>
                      </div>

                      {/* Group items */}
                      {group.map((result) => {
                        const globalIdx = flatResults.indexOf(result);
                        const isActive = globalIdx === activeIndex;

                        return (
                          <button
                            key={result.id}
                            onClick={() => handleSelect(result)}
                            onMouseEnter={() => setActiveIndex(globalIdx)}
                            className={cn(
                              'w-full flex items-start gap-3 px-4 py-2.5 text-left transition-colors',
                              isActive ? 'bg-accent' : 'hover:bg-accent/50',
                            )}
                          >
                            <Icon className={cn('h-4 w-4 mt-0.5 flex-shrink-0', config.color)} />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">
                                {result.title}
                              </p>
                              {result.subtitle && (
                                <p className="text-xs text-muted-foreground truncate mt-0.5">
                                  {result.subtitle}
                                </p>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer hint */}
          {hasResults && (
            <div className="border-t border-border px-4 py-2 flex items-center gap-4 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1">
                <kbd className="px-1 border border-border rounded text-[10px]">↑↓</kbd>
                {t('search.navigate', 'navigate')}
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 border border-border rounded text-[10px]">↵</kbd>
                {t('search.select', 'select')}
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 border border-border rounded text-[10px]">esc</kbd>
                {t('search.close', 'close')}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
